<template>
  <div class="s4">
    <!-- Expired or invalid link -->
    <div v-if="tokenValid === false" class="s4-expired">
      <BaseIcon icon="exclamation-triangle" class="s4-expired-icon" />
      <h1 class="s4-expired-title">{{ t("safe4_track.expired_title") }}</h1>
      <p class="s4-expired-text">{{ t("safe4_track.expired_text") }}</p>
    </div>

    <template v-else>

      <!-- Brand bar only: Securdia dark blue with the white logo (final feedback, 2026-09-14) -->
      <header class="s4-header">
        <img :src="require('@/assets/logos/logo_menu.png')" alt="Securdia" class="s4-logo" />
        <h1 class="s4-sr">{{ t("safe4_track.page_heading") }}</h1>
      </header>

      <div class="s4-body">
        <div class="s4-map">
          <GmapMap :center="center" :zoom="zoom" map-type-id="roadmap" class="s4-gmap" ref="map">
            <GmapMarker v-if="marker" :position="marker.position" :clickable="false" :draggable="false" :icon="{ url: marker.icon }"></GmapMarker>
            <GmapCircle
              v-if="alarmCircle"
              :center="alarmCircle.center"
              :radius="50"
              :visible="true"
              :options="{ fillColor: '#DC2626', fillOpacity: 0.28, strokeColor: '#DC2626', strokeOpacity: 0.85, strokeWeight: 2 }"
            ></GmapCircle>
          </GmapMap>
        </div>

        <aside class="s4-panel">
          <div v-if="loading" class="s4-loading">{{ t("safe4_track.loading") }}</div>

          <template v-else-if="alarm">
            <!-- Identity and live state -->
            <section class="s4-seg s4-seg-lead">
              <!-- Clicking the name pans and zooms the map to the alarm (feedback 2026-09-15) -->
              <h2 class="s4-name">
                <button v-if="position" type="button" class="s4-name-btn" :title="t('safe4_track.show_on_map')" @click="focusOnAlarm">{{ alarm.unit_name }}</button>
                <template v-else>{{ alarm.unit_name }}</template>
              </h2>

              <p class="s4-ping">
                <span class="s4-dot" :class="{ 's4-dot-stale': positionIsStale }" :title="positionIsStale ? t('safe4_track.position_stale') : ''"></span>
                <span class="s4-ping-time">{{ lastReportedRelative || t("safe4_track.unknown") }}</span>
                <span class="s4-ping-label">&middot; {{ t("safe4_track.last_ping") }}</span>
              </p>

              <ul class="s4-metrics">
                <li class="s4-metric" v-if="gsmSignal" :title="t('safe4_track.gsm')">
                  <BaseIcon icon="signal" /><span><span class="s4-sr">{{ t("safe4_track.gsm") }}: </span>{{ gsmSignal }}</span>
                </li>
                <!-- Battery as a bar, not a figure: the percentage is not reliable on every device -->
                <li class="s4-metric" v-if="batteryPercent !== null" :title="t('safe4_track.battery')">
                  <span class="s4-battery" :class="{ 's4-battery-low': batteryLevel === 'low' }" aria-hidden="true">
                    <span class="s4-battery-fill" :style="{ width: batteryFillWidth }"></span>
                  </span>
                  <span class="s4-sr">{{ t("safe4_track.battery") }}: {{ t(`safe4_track.battery_${batteryLevel}`) }}</span>
                </li>
                <li class="s4-metric" v-if="speed !== null" :title="t('safe4_track.speed')">
                  <BaseIcon icon="tachometer-alt" /><span><span class="s4-sr">{{ t("safe4_track.speed") }}: </span>{{ speed }} {{ t("safe4_track.speed_unit") }}</span>
                </li>
                <li class="s4-metric" v-if="accuracyM !== null" :title="t('safe4_track.accuracy')">
                  <BaseIcon icon="crosshairs" /><span><span class="s4-sr">{{ t("safe4_track.accuracy") }}: </span>&plusmn;{{ accuracyM }} m</span>
                </li>
                <li class="s4-metric s4-metric-coords" v-if="coordinatesText" :title="t('safe4_track.coordinates')">
                  <BaseIcon icon="location-arrow" /><span><span class="s4-sr">{{ t("safe4_track.coordinates") }}: </span>{{ coordinatesShort }}</span>
                  <button type="button" class="s4-icon s4-icon-xs" :class="{ 's4-icon-done': copiedField === 'coordinates' }"
                    :title="t('safe4_track.copy_coordinates')" :aria-label="t('safe4_track.copy_coordinates')"
                    @click="copyValue('coordinates', coordinatesText)">
                    <BaseIcon :icon="copiedField === 'coordinates' ? 'check' : 'copy'" />
                  </button>
                </li>
              </ul>

              <!-- Open the position in a maps app. Was a text link at the very bottom; now a button up here. -->
              <a v-if="position" :href="getMapLink(checkIOS() ? 'apple' : 'google', position)" target="_blank" rel="noopener" class="s4-btn">
                <BaseIcon icon="external-link-alt" class="s4-btn-ic" />{{ t(checkIOS() ? "safe4_track.open_apple_maps" : "safe4_track.open_google_maps") }}
              </a>

              <p class="s4-caption" v-if="!position">{{ t("safe4_track.no_position") }}</p>
            </section>

            <!-- Nearest street address -->
            <section class="s4-seg">
              <h3 class="s4-label">{{ t("safe4_track.nearest_street_address") }}</h3>
              <p class="s4-caption"><BaseIcon icon="info-circle" class="s4-caption-ic" />{{ t("safe4_track.address_note") }}</p>

              <div class="s4-box" :class="{ 's4-box-empty': !addressLine }">
                <span class="s4-box-value" v-if="addressLine">{{ addressLine }}</span>
                <span class="s4-box-muted" v-else-if="geocoding">{{ t("safe4_track.resolving_address") }}</span>
                <span class="s4-box-muted" v-else>{{ t("safe4_track.address_unavailable") }}</span>
                <button type="button" class="s4-icon" :class="{ 's4-icon-done': copiedField === 'address' }" :disabled="!addressLine"
                  :title="t('safe4_track.copy_address')" :aria-label="t('safe4_track.copy_address')"
                  @click="copyValue('address', addressLine)">
                  <BaseIcon :icon="copiedField === 'address' ? 'check' : 'copy'" />
                </button>
              </div>

              <div class="s4-box s4-box-sm" v-if="postalCode">
                <span class="s4-box-value"><span class="s4-box-key">{{ t("safe4_track.postal_code") }}</span> {{ postalCode }}</span>
                <button type="button" class="s4-icon s4-icon-xs" :class="{ 's4-icon-done': copiedField === 'postalCode' }"
                  :title="t('safe4_track.copy_postal_code')" :aria-label="t('safe4_track.copy_postal_code')"
                  @click="copyValue('postalCode', postalCode)">
                  <BaseIcon :icon="copiedField === 'postalCode' ? 'check' : 'copy'" />
                </button>
              </div>

              <p class="s4-row-info" v-if="distanceText">
                <BaseIcon icon="route" class="s4-row-ic" />
                <span v-html="distanceHtml"></span>
              </p>
            </section>

            <!-- SecurPos temporary visit -->
            <section class="s4-seg" v-if="securposVisit">
              <h3 class="s4-label s4-label-caps">
                <BaseIcon icon="clock" class="s4-label-ic s4-ic-securpos" />{{ t("safe4_track.securpos_title") }}
                <span class="s4-badge s4-badge-amber">{{ t("safe4_track.securpos_badge") }}</span>
              </h3>
              <dl class="s4-rows">
                <template v-if="securposTimeRange">
                  <dt>{{ t("safe4_track.securpos_time") }}</dt><dd>{{ securposTimeRange }}</dd>
                </template>
                <template v-if="securposVisitor">
                  <dt>{{ t("safe4_track.securpos_visitor") }}</dt><dd>{{ securposVisitor }}</dd>
                </template>
              </dl>
              <div class="s4-box" v-if="securposAddress">
                <span class="s4-box-value">{{ securposAddress }}</span>
                <button type="button" class="s4-icon" :class="{ 's4-icon-done': copiedField === 'securposAddress' }"
                  :title="t('safe4_track.copy_visit_address')" :aria-label="t('safe4_track.copy_visit_address')"
                  @click="copyValue('securposAddress', securposAddress)">
                  <BaseIcon :icon="copiedField === 'securposAddress' ? 'check' : 'copy'" />
                </button>
              </div>
            </section>

            <!-- Indoor position from the present beacon -->
            <section class="s4-seg" v-if="tag">
              <h3 class="s4-label s4-label-caps">
                <BaseIcon icon="broadcast-tower" class="s4-label-ic s4-ic-beacon" />{{ t("safe4_track.indoor_position") }}
                <span class="s4-badge s4-badge-blue">{{ t("safe4_track.indoor_badge") }}</span>
              </h3>
              <p class="s4-strong">{{ indoor ? indoor.zone : tag.name }}</p>
              <p class="s4-caption">
                <template v-if="indoor">{{ tag.name }}</template>
                <template v-else>{{ t("safe4_track.tag_title") }}</template>
                <template v-if="indoorSignalAge"> &middot; {{ t("safe4_track.signal_age", { age: indoorSignalAge }) }}</template>
              </p>
            </section>

            <!-- Close alarm -->
            <section class="s4-seg s4-seg-close">
              <div class="s4-close-box">
              <h3 class="s4-heading">{{ t("safe4_track.close_alarm") }}</h3>
              <p class="s4-caption">{{ t("safe4_track.close_alarm_note") }}</p>
              <div class="s4-classify">
                <button type="button" v-for="option in classifications" :key="option.value" class="s4-classify-btn"
                  :class="{ 's4-classify-active': classifiedAs === option.value }" :disabled="classifying"
                  @click="classifyAlarm(option.value)">
                  <span class="s4-dot" :class="`s4-dot-${option.value}`"></span>{{ option.label }}
                </button>
              </div>
              <!-- Always rendered with a reserved height, so the band does not jump when text appears -->
              <p class="s4-caption s4-classify-status" :class="{ 's4-err': classifyError, 's4-ok': !classifyError && classifiedAs }" aria-live="polite">
                <template v-if="classifyError">{{ classifyError }}</template>
                <template v-else-if="classifiedAs">{{ t("safe4_track.classify_saved") }}</template>
              </p>
              </div>
            </section>
          </template>

          <div v-else class="s4-loading">{{ t("safe4_track.no_data") }}</div>
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
import { gmapApi } from "vue2-google-maps";

export default {
  name: "Safe4TrackAlarm",
  title() {
    return "Sporing | SecurCloud";
  },
  data() {
    return {
      token: this.$route.params.token,
      tokenValid: null,
      loading: true,

      alarm: null,
      gps_alarm: null,
      tag: null,
      securposVisit: null,
      indoor: null,
      classifying: false,
      classifiedAs: "",
      classifyError: "",

      // Registered address of the unit/customer, and the point it geocodes to.
      // Geocoded once per address, then the distance is recomputed locally on
      // every refresh — no further API calls as the device moves.
      registeredAddress: null,
      registeredPos: null,
      registeredGeocodedFor: "",
      distanceM: null,

      zoom: 16,
      center: this.$store.state.mapCenter,
      marker: null,
      alarmCircle: null,
      firstLoad: true,

      // Reverse geocoding results, kept between refreshes so a standing alarm
      // is only looked up once (see maybeResolveAddress).
      address: "",
      postalTown: "",
      postalCode: "",
      geocoding: false,
      lastGeocodedPos: null,

      // SEC-331: the page renders in its OWN locale rather than the app's global
      // $i18n.locale. The global is shared with the rest of the app (App.vue and
      // the locale switcher both write it), and an operator must never be shown
      // this page in the wrong language. Resolved once, from the URL.
      pageLocale: (() => {
        const requested = String((this.$route.query && this.$route.query.lang) || "").toLowerCase();
        return ["no", "sv", "en"].indexOf(requested) > -1 ? requested : "no";
      })(),

      copiedField: null,
      copiedTimer: null,

      refreshIntervalRef: null,
      refreshInterval: 30000, // every 30s, same as the generic tracking page
    };
  },

  computed: {
    google: gmapApi,

    /** Latest reported position, or null when the device never reported one. */
    position() {
      if (!this.gps_alarm || !this.gps_alarm.gps_pos) return null;
      const { lat, lng } = this.gps_alarm.gps_pos;
      if (typeof lat !== "number" || typeof lng !== "number") return null;
      if (!lat && !lng) return null;
      return { lat: lat, lng: lng };
    },

    coordinatesText() {
      if (!this.position) return "";
      return `${this.position.lat.toFixed(6)}, ${this.position.lng.toFixed(6)}`;
    },

    /** True while the push button was pressed less than 30 minutes ago. */
    alarmTriggered() {
      if (!this.gps_alarm || !this.gps_alarm.lastPushButtonTimestamp) return false;
      return new Date().getTime() - this.gps_alarm.lastPushButtonTimestamp < 1800000;
    },

    lastReportedRelative() {
      if (!this.gps_alarm || !this.gps_alarm.updatedAt) return "";
      return this.mo(this.gps_alarm.updatedAt).fromNow();
    },

    /** Older than one hour, the same threshold the generic page uses. */
    positionIsStale() {
      if (!this.gps_alarm || !this.gps_alarm.updatedAt) return false;
      return this.moment().diff(this.moment(this.gps_alarm.updatedAt)) > 1000 * 60 * 60;
    },

    batteryPercent() {
      if (!this.gps_alarm) return null;
      if (this.alarm && new RegExp(/^SRT/g).test(this.alarm.type) && this.gps_alarm.battery_voltage) {
        return Math.min(Math.floor(this.gps_alarm.battery_voltage * 100 - 323), 100);
      }
      return typeof this.gps_alarm.battery_percent === "number" ? this.gps_alarm.battery_percent : null;
    },

    /**
     * Coarse battery level for the bar and its screen-reader text. The figure
     * itself is not shown: it is not reliable on every device (final feedback,
     * 2026-09-14), so the page only says low / medium / high.
     */
    batteryLevel() {
      if (this.batteryPercent === null) return "";
      if (this.batteryPercent <= 20) return "low";
      if (this.batteryPercent <= 60) return "medium";
      return "high";
    },

    /** Fill width of the battery bar, clamped to 0-100 %. */
    batteryFillWidth() {
      if (this.batteryPercent === null) return "0%";
      return `${Math.min(100, Math.max(0, this.batteryPercent))}%`;
    },

    gsmSignal() {
      if (!this.gps_alarm || typeof this.gps_alarm.gsm_signal !== "number") return "";
      // Conversion as per SRT documentation, same as the generic page.
      if (this.gps_alarm.gsm_signal <= 31) return Math.ceil(3.225 * this.gps_alarm.gsm_signal) + " %";
      return "N/A";
    },

    speed() {
      if (!this.gps_alarm || typeof this.gps_alarm.alarm_speed !== "number") return null;
      if (this.alarm && new RegExp(/^SRT/g).test(this.alarm.type)) return Number((this.gps_alarm.alarm_speed * 1.852).toFixed(1));
      return this.gps_alarm.alarm_speed;
    },

    /** Short coordinates for the metric row; the copy control still copies full precision. */
    coordinatesShort() {
      if (!this.position) return "";
      return `${this.position.lat.toFixed(4)}, ${this.position.lng.toFixed(4)}`;
    },

    /**
     * One address line: street, postcode and postal town together, as the
     * 2026-09-11 design review asks. Whatever resolved is shown; the postcode
     * also gets its own copy control below.
     */
    addressLine() {
      const tail = [this.postalCode, this.postalTown].filter(Boolean).join(" ");
      return [this.address, tail].filter(Boolean).join(", ");
    },

    /**
     * Distance from the alarm to the registered address, e.g. "1,8 km".
     * Computed server side from a cached point, so it costs no API call here.
     */
    distanceText() {
      if (this.distanceM === null) return "";
      if (this.distanceM < 1000) return `${this.distanceM} m`;
      const km = this.distanceM / 1000;
      const digits = km < 10 ? 1 : 0;
      return `${km.toLocaleString(this.pageLocale === "en" ? "en-GB" : "nb-NO", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })} km`;
    },

    /** "Storgata 22, 0184 Oslo" from the separately stored fields. */
    registeredAddressText() {
      const a = this.registeredAddress;
      if (!a) return "";
      const street = (a.address || "").trim();
      const tail = [(a.zip_code || "").trim(), (a.city || "").trim()].filter(Boolean).join(" ");
      if (!street && !tail) return "";
      return [street, tail].filter(Boolean).join(", ");
    },

    /** The three classifications an operator can set. */
    classifications() {
      return [
        { value: "test", label: this.t("safe4_track.class_test") },
        { value: "false", label: this.t("safe4_track.class_false") },
        { value: "confirmed", label: this.t("safe4_track.class_confirmed") },
      ];
    },

    /** How fresh the indoor fix is: "12 s" under a minute, otherwise "3 min". */
    indoorSignalAge() {
      if (!this.indoor || !this.indoor.signalAt) return "";
      const seconds = Math.max(0, Math.round((Date.now() - new Date(this.indoor.signalAt).getTime()) / 1000));
      if (seconds < 60) return `${seconds} s`;
      return `${Math.round(seconds / 60)} ${this.t("safe4_track.minutes_short_unit")}`;
    },

    /** Distance sentence with the figure emphasised, as in the draft. */
    distanceHtml() {
      if (!this.distanceText) return "";
      const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
      return esc(this.t("safe4_track.distance_from_registered", { distance: "\u0000" })).replace("\u0000", `<strong>${esc(this.distanceText)}</strong>`);
    },

    securposVisitor() {
      return (this.securposVisit && this.securposVisit.address && this.securposVisit.address.visitor) || "";
    },

    /** "i dag 11:26-12:56", or with the date when the visit did not start today. */
    securposTimeRange() {
      if (!this.securposVisit || !this.securposVisit.startTime) return "";
      const start = this.mo(this.securposVisit.startTime);
      const end = this.securposEndTime ? this.mo(this.securposEndTime) : null;
      const startsToday = start.isSame(this.mo(), "day");
      const head = startsToday ? this.t("safe4_track.securpos_today") : start.format("DD.MM.YYYY");
      return end ? `${head} ${start.format("HH:mm")}\u2013${end.format("HH:mm")}` : `${head} ${start.format("HH:mm")}`;
    },

    /**
     * Position accuracy in metres (SEC-331 design review, "±X m").
     *
     * `gps.hdop` is NOT the same quantity on every device, so it cannot be shown
     * as-is: TWIG and EMERIT write the device's own accuracy in METRES into that
     * field, while SRT and TELTONIKA write raw HDOP, which is dimensionless. An
     * SRT hdop of 1.2 is roughly ±36 m, not ±1 m.
     *
     * The SRT/Teltonika conversion mirrors helpers.srtGpsAccuracy in the TCP
     * server: hdop x user range error, 30 with a good satellite count, else 70.
     *
     * Unknown device types return null — no figure is better than a wrong one.
     */
    accuracyM() {
      if (!this.gps_alarm || !this.alarm) return null;

      const hdop = this.gps_alarm.hdop;
      if (typeof hdop !== "number" || hdop <= 0) return null; // -1 / 0 = no fix

      const type = this.alarm.type || "";

      if (/^TWIG/i.test(type) || /^EMERIT/i.test(type)) return Math.round(hdop);

      if (/^SRT/i.test(type) || /^TELTONIKA/i.test(type)) {
        const satellites = this.gps_alarm.satellites;
        const userRangeError = typeof satellites === "number" && satellites >= 5 ? 30 : 70;
        return Math.ceil(hdop * userRangeError);
      }

      return null;
    },

    /** SecurPos visiting address, built from the parts that are filled in. */
    securposAddress() {
      if (!this.securposVisit || !this.securposVisit.address) return "";
      const a = this.securposVisit.address;
      return [a.area, a.building, a.floor].filter(Boolean).join(", ");
    },

    securposEndTime() {
      if (!this.securposVisit || !this.securposVisit.startTime || !this.securposVisit.timeLength) return null;
      return new Date(new Date(this.securposVisit.startTime).getTime() + this.securposVisit.timeLength * 1000);
    },
  },

  methods: {
    /** Translate in the page's own locale, never the app's global one. */
    t(key, values) {
      return values ? this.$t(key, this.pageLocale, values) : this.$t(key, this.pageLocale);
    },

    /** A moment in the page's own locale (nb for Norwegian Bokmal). */
    mo(value) {
      const ml = this.pageLocale === "no" ? "nb" : this.pageLocale;
      return (value === undefined ? this.moment() : this.moment(value)).locale(ml);
    },

    /** Set the tab title in the page's locale (the title mixin runs earlier). */
    applyPageTitle() {
      document.title = `${this.t("safe4_track.page_heading")} | SecurCloud`;
    },

    /** Google wants a language code for the address text. */
    googleLanguage() {
      return this.pageLocale;
    },

    async getAlarm() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/gps/safe4-alarm/${this.token}`);

        if (!response.data.success) {
          this.tokenValid = false;
          this.loading = false;
          return;
        }

        this.tokenValid = true;
        this.alarm = response.data.alarm;
        this.gps_alarm = response.data.gps_alarm;
        this.tag = response.data.tag || null;
        this.securposVisit = response.data.securpos_visit || null;
        this.registeredAddress = response.data.registered_address || null;
        this.indoor = response.data.indoor || null;
        // response.data.recent_events is still sent by the API; the block is parked (final feedback, 2026-09-14)
        this.loading = false;

        this.updateMap();
        this.maybeResolveAddress();
        this.maybeResolveRegisteredAddress();
        this.updateDistance();
      } catch (error) {
        this.loading = false;
        // Keep whatever is already on screen; a refresh may simply have failed.
        if (this.tokenValid === null) this.tokenValid = false;
        this.handleError(error, "");
      }
    },

    updateMap() {
      if (!this.position) {
        this.marker = null;
        this.alarmCircle = null;
        return;
      }

      this.marker = { icon: this.markerIcon(), position: this.position };

      // Red circle around a device that triggered in the last 30 minutes.
      this.alarmCircle = this.alarmTriggered ? { center: this.position } : null;

      // Centre on the first position only. After that the marker moves but the
      // map stays where the operator left it.
      if (this.firstLoad) {
        this.firstLoad = false;
        this.center = this.position;

        if (this.$refs.map) {
          this.$refs.map.$mapPromise.then((map) => {
            map.panTo(this.position);
            map.setZoom(16);
          });
        }
      }
    },

    /** Pan and zoom the map to the alarm when the operator clicks its name. */
    focusOnAlarm() {
      if (!this.position || !this.$refs.map) return;
      this.$refs.map.$mapPromise.then((map) => {
        map.panTo(this.position);
        map.setZoom(18);
      });
    },

    /** Same icon set as the generic tracking page. */
    markerIcon() {
      const type = (this.alarm && this.alarm.type) || "";

      if (type == "SRT430") return require("@/assets/icon/small_SRT430.png");
      if (type == "SRT341") {
        const hasDiesel = typeof this.alarm.unit_name === "string" && /diesel/i.test(this.alarm.unit_name);
        return hasDiesel ? require("@/assets/icon/SRT341_diesel.png") : require("@/assets/icon/srt341.png");
      }
      if (type == "SRT406" || type == "SRT406i") return require("@/assets/icon/small_SRT406.png");
      if (new RegExp(/^TELTONIKA/g).test(type)) return require("@/assets/icon/car.png");
      if (new RegExp(/^TWIG/g).test(type)) {
        if (type == "TWIG One EX") return require("@/assets/icon/twig_one_ex.png");
        if (/^TWIG One/.test(type)) return require("@/assets/icon/twig_one.png");
        if (type == "TWIG Eon") return require("@/assets/icon/TwigSmallEon.png");
        return require("@/assets/icon/twig_map_marker.png");
      }
      return require("@/assets/icon/new_small_map_marker.png");
    },

    /**
     * Metres between two points, via the Google `geometry` library the app
     * already loads. Accepts plain {lat,lng} or a google.maps.LatLng.
     * Returns Infinity when the library is not ready, so movement checks fail
     * open rather than suppressing a lookup.
     */
    metresBetween(a, b) {
      const geometry = this.google && this.google.maps && this.google.maps.geometry;
      if (!geometry || !a || !b) return Infinity;
      const toLatLng = (p) => (typeof p.lat === "function" ? p : new this.google.maps.LatLng(p.lat, p.lng));
      return geometry.spherical.computeDistanceBetween(toLatLng(a), toLatLng(b));
    },

    /**
     * Reverse geocode the latest position. The page refreshes every 30s, so the
     * lookup only runs on the first position and after the device has actually
     * moved more than 25 metres. A standing alarm therefore costs one call.
     */
    async maybeResolveAddress() {
      const pos = this.position;
      if (!pos) return;
      if (!this.google || !this.google.maps) return; // maps script not ready yet
      if (this.geocoding) return;

      // Has it actually moved far enough to be worth another lookup?
      if (this.lastGeocodedPos && this.metresBetween(pos, this.lastGeocodedPos) < 25) return;

      this.geocoding = true;
      try {
        const geocoder = new this.google.maps.Geocoder();
        const result = await geocoder.geocode({ location: pos, language: this.googleLanguage() });
        const results = (result && result.results) || [];

        if (results.length) {
          this.applyGeocodeResults(results);
          this.lastGeocodedPos = pos;
        } else {
          // No result for these coordinates: keep the coordinates visible and
          // show the fallback text instead of an address.
          this.clearAddress();
          this.lastGeocodedPos = pos;
        }
      } catch (error) {
        // Quota, network or a blocked key: never break the page.
        console.error("SEC-331 reverse geocoding failed:", error);
        this.clearAddress();
      } finally {
        this.geocoding = false;
      }
    },

    /**
     * Forward-geocode the registered address, once per address. The distance
     * itself is then local, so a moving device costs nothing extra.
     */
    async maybeResolveRegisteredAddress() {
      const addressText = this.registeredAddressText;
      if (!addressText) return;
      if (this.registeredGeocodedFor === addressText) return; // already tried
      if (!this.google || !this.google.maps) return; // maps script not ready yet

      this.registeredGeocodedFor = addressText;

      try {
        const geocoder = new this.google.maps.Geocoder();
        const result = await geocoder.geocode({ address: addressText, language: this.googleLanguage() });
        const first = result && result.results && result.results[0];
        const loc = first && first.geometry && first.geometry.location;

        // Keep Google's LatLng as-is; computeDistanceBetween takes it directly.
        if (loc) this.registeredPos = loc;
      } catch (error) {
        // Quota, network, or an address Google cannot place: just no distance.
        console.error("SEC-331 registered address geocoding failed:", error);
        this.registeredPos = null;
      }

      this.updateDistance();
    },

    /**
     * Local distance between the alarm and the registered address, via the
     * Google `geometry` library the app already loads (main.js libraries list).
     * No hand-rolled haversine and no API call.
     */
    updateDistance() {
      const geometry = this.google && this.google.maps && this.google.maps.geometry;
      if (!this.position || !this.registeredPos || !geometry) {
        this.distanceM = null;
        return;
      }

      this.distanceM = Math.round(this.metresBetween(this.position, this.registeredPos));
    },

    clearAddress() {
      this.address = "";
      this.postalTown = "";
      this.postalCode = "";
    },

    /**
     * Pull street address, postal town and postal code out of the results.
     * Returns false when nothing usable came back, so the caller can show the
     * "no address" fallback instead.
     */
    applyGeocodeResults(results) {
      const pick = (result, type) => {
        if (!result || !result.address_components) return "";
        const found = result.address_components.find((c) => c.types.indexOf(type) > -1);
        return found ? found.long_name : "";
      };

      // Out at sea or far from any road, Google answers with a Plus Code
      // ("89222222+22") instead of no result at all. That is not an address an
      // operator can act on, so those results are dropped and the page shows the
      // "no address" fallback.
      const isPlusCode = (text) => /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,}$/i.test(String(text).trim());
      const usable = results.filter((r) => !(r.types && r.types.indexOf("plus_code") > -1));

      if (!usable.length) {
        this.clearAddress();
        return false;
      }

      // Prefer a result that is an actual street address.
      const best =
        usable.find((r) => r.types && r.types.indexOf("street_address") > -1) ||
        usable.find((r) => r.types && r.types.indexOf("premise") > -1) ||
        usable[0];

      const route = pick(best, "route");
      const number = pick(best, "street_number");

      if (route) {
        this.address = number ? `${route} ${number}` : route;
      } else {
        const firstLine = (best.formatted_address || "").split(",")[0].trim();
        this.address = isPlusCode(firstLine) ? "" : firstLine;
      }

      // The postal town is not always on the same result as the street, so all
      // results are scanned for it.
      let town = "";
      let code = "";
      for (const r of usable) {
        if (!town) town = pick(r, "postal_town") || pick(r, "locality") || pick(r, "sublocality") || pick(r, "administrative_area_level_2");
        if (!code) code = pick(r, "postal_code");
        if (town && code) break;
      }
      this.postalTown = town;
      this.postalCode = code;

      // Nothing an operator can use.
      if (!this.address && !this.postalTown) {
        this.clearAddress();
        return false;
      }
      return true;
    },

    /** Classify the alarm the operator has just handled. */
    async classifyAlarm(value) {
      if (this.classifying) return;
      this.classifying = true;

      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_SERVER_URL}/gps/safe4-alarm/classify/${this.token}`,
          { classification: value }
        );

        if (response.data && response.data.success) {
          this.classifiedAs = value;
          this.classifyError = "";
          this.handleSuccess(this.t("safe4_track.classify_saved"), "top-right", 2000);
          this.getAlarm(); // refresh so the page reflects the saved state
        } else if (response.data && response.data.reason === "no_recent_alarm") {
          // The server found no alarm inside its window to attach the classification to.
          const hours = response.data.window_hours || 24;
          this.classifyError = this.t("safe4_track.classify_no_recent", { hours: hours });
        } else {
          this.classifyError = this.t("safe4_track.classify_failed");
        }
      } catch (error) {
        this.classifyError = this.t("safe4_track.classify_failed");
        this.handleError(error, "");
      } finally {
        this.classifying = false;
      }
    },

    /** Copy one field and confirm it, both on the button and as a toast. */
    async copyValue(field, text) {
      if (!text) return;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for browsers without the clipboard API.
          const helper = document.createElement("textarea");
          helper.value = text;
          helper.setAttribute("readonly", "");
          helper.style.position = "absolute";
          helper.style.left = "-9999px";
          document.body.appendChild(helper);
          helper.select();
          document.execCommand("copy");
          document.body.removeChild(helper);
        }

        this.copiedField = field;
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => {
          this.copiedField = null;
        }, 2000);

        this.handleSuccess(this.t("safe4_track.copied_message"), "top-right", 1500);
      } catch (error) {
        this.handleError(null, this.t("safe4_track.copy_failed"));
      }
    },

    startAutoRefresh() {
      this.refreshIntervalRef = setInterval(() => {
        if (this.tokenValid) this.getAlarm();
      }, this.refreshInterval);
    },
  },

  created() {
    this.applyPageTitle();
    this.getAlarm();
  },

  mounted() {
    this.startAutoRefresh();

    // The first fetch can finish before the Google Maps script has loaded, and
    // the geocoder needs it. Try again as soon as the map is ready.
    this.$nextTick(() => {
      if (this.$refs.map) {
        this.$refs.map.$mapPromise.then(() => {
          this.maybeResolveAddress();
          this.maybeResolveRegisteredAddress();
        });
      }
    });
  },

  beforeDestroy() {
    clearInterval(this.refreshIntervalRef);
    clearTimeout(this.copiedTimer);
  },
};
</script>

<style scoped>
/*
 * SEC-331, design review 2026-09-11 — built to Rasmus's draft: one blue accent
 * (the alarm name), everything else ink and grey; thin borders only where they
 * mean "this is a value you can take" (address boxes, controls).
 *
 * Final feedback 2026-09-14: brand bar in Securdia dark blue with the white
 * logo; no badges, no "last updated", no recent-events list, no bottom link;
 * battery as a bar; more air between and inside the segments.
 */
.s4 {
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  color: #23272c;
  font-family: "Source Sans Pro", "Source Sans 3", system-ui, sans-serif;
}

.s4-expired {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
}

.s4-expired-icon { font-size: 32px; color: #b42318; margin-bottom: 14px; }
.s4-expired-title { font-size: 19px; font-weight: 600; margin: 0 0 6px; }
.s4-expired-text { margin: 0; color: #6b7075; font-size: 13.5px; }

/* brand bar: Securdia dark blue (tailwind securdia.dark-blue) with the white logo */
.s4-header {
  display: flex;
  align-items: center;
  padding: 11px 22px;
  background: #1c497c;
  flex-shrink: 0;
}
.s4-logo { height: 26px; width: auto; display: block; }

.s4-body { flex: 1; display: flex; min-height: 0; }
.s4-map { flex: 1; min-height: 320px; }
.s4-gmap { width: 100%; height: 100%; min-height: 320px; }

.s4-panel {
  width: 360px;
  flex-shrink: 0;
  border-left: 1px solid #e6e8eb;
  overflow-y: auto;
  padding: 0 0 8px;
  display: flex;
  flex-direction: column;
}

/* desktop: the page is exactly one viewport tall, so the panel fills the space under
   the header and its bottom-pinned sections have something to pin against */
@media (min-width: 1024px) {
  .s4 { height: 100vh; }
}
.s4-seg { flex-shrink: 0; }

.s4-loading { padding: 22px 18px; color: #6b7075; font-size: 13px; }

/* segments: a thin rule after each one, generous padding */
.s4-seg { padding: 18px 22px; border-bottom: 1px solid #e6e8eb; }
.s4-seg-lead { padding-top: 22px; }
.s4-seg-close { padding: 18px 0 0; margin-top: auto; border-bottom: 0; }

/* the one accent on the page */
.s4-name {
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  color: #2563eb;
  line-height: 1.3;
  margin: 0;
  word-break: break-word;
}
.s4-name-btn {
  font: inherit;
  color: inherit;
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
  border-radius: 3px;
}
.s4-name-btn:hover { text-decoration: underline; text-underline-offset: 3px; }
.s4-name-btn:focus-visible { outline: 2px solid #7a8492; outline-offset: 2px; }

.s4-ping {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin: 6px 0 0;
  font-size: 13px;
  color: #4a4f55;
}
.s4-ping-label { color: #6b7075; }

.s4-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #15803d;
  flex-shrink: 0;
}
.s4-dot-stale { background: #b45309; }
.s4-dot-test { background: #15803d; }
.s4-dot-false { background: #ca8a04; }
.s4-dot-confirmed { background: #b42318; }

.s4-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
}
.s4-metric {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  color: #4a4f55;
  font-variant-numeric: tabular-nums;
}
.s4-metric >>> svg { color: #8b9099; font-size: 11px; }
.s4-metric-coords { color: #6b7075; }

/* battery bar: outline with a nub, filled to the level; red only when low */
.s4-battery {
  position: relative;
  display: inline-block;
  width: 22px;
  height: 11px;
  border: 1.5px solid #8b9099;
  border-radius: 2.5px;
  padding: 1.5px;
  box-sizing: border-box;
}
.s4-battery::after {
  content: "";
  position: absolute;
  right: -4px;
  top: 2px;
  width: 2.5px;
  height: 4px;
  background: #8b9099;
  border-radius: 0 1px 1px 0;
}
.s4-battery-fill {
  display: block;
  height: 100%;
  min-width: 1px;
  background: #4a4f55;
  border-radius: 1px;
}
.s4-battery-low { border-color: #b42318; }
.s4-battery-low::after { background: #b42318; }
.s4-battery-low .s4-battery-fill { background: #b42318; }

/* open-in-maps: a quiet outlined button under the metrics */
.s4-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 7px 12px;
  font-size: 12.5px;
  font-weight: 500;
  color: #23272c;
  background: #ffffff;
  border: 1px solid #d9dde2;
  border-radius: 4px;
  text-decoration: none;
  transition: border-color 0.12s ease;
}
.s4-btn:hover { border-color: #8b9099; color: #23272c; }
.s4-btn:focus-visible { outline: 2px solid #7a8492; outline-offset: 1px; }
.s4-btn-ic { color: #8b9099; font-size: 11px; }

.s4-sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}

/* section labels, sentence case */
.s4-label {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #3d4249;
  margin: 0;
  line-height: 1.3;
}
.s4-label-ic { font-size: 14px; }

/* SecurPos and indoor blocks follow Rasmus's crop (2026-09-15): a small
   tracked uppercase label, a coloured icon in Securdia orange / blue, and a
   tinted badge from the brand palette (accent-100/800, primary-100/700). */
.s4-label-caps {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7075;
  gap: 6px 8px;
}
.s4-label-caps .s4-label-ic { font-size: 13px; }
.s4-ic-securpos { color: #f29100; }
.s4-ic-beacon { color: #1a8de9; }

.s4-badge {
  display: inline-block;
  white-space: nowrap;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
  border-radius: 4px;
  padding: 2px 7px;
  line-height: 1.5;
}
.s4-badge-amber { background: #feebc8; color: #8f5500; }
.s4-badge-blue { background: #d4eafb; color: #1c497c; }

/* Close alarm is the one sentence-case heading in the draft */
.s4-heading { font-size: 14px; font-weight: 600; color: #3d4249; margin: 0; line-height: 1.3; }

.s4-caption {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 12.5px;
  color: #666c73;
  margin: 5px 0 0;
  line-height: 1.4;
}
.s4-caption-ic { font-size: 11px; position: relative; top: 1px; }
.s4-ok { color: #15803d; }
.s4-err { color: #b42318; }

.s4-strong { font-size: 13.5px; font-weight: 600; margin: 8px 0 0; line-height: 1.35; word-break: break-word; }

/* a value you can take: thin border, icon inside */
.s4-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
  padding: 9px 12px;
  border: 1px solid #d9dde2;
  border-radius: 4px;
}
.s4-box-empty { border-style: dashed; }
.s4-box-sm { display: inline-flex; width: auto; margin-top: 10px; padding: 5px 10px; gap: 8px; }
.s4-box-sm .s4-box-value { font-size: 12.5px; }
.s4-box-key { color: #666c73; font-weight: 400; }
.s4-box-value { font-size: 13.5px; font-weight: 500; line-height: 1.35; word-break: break-word; }
.s4-box-muted { font-size: 12.5px; color: #6b7075; }

.s4-icon {
  flex-shrink: 0;
  border: 0;
  background: none;
  padding: 2px;
  line-height: 1;
  color: #a9aeb4;
  font-size: 12px;
  cursor: pointer;
  border-radius: 3px;
  transition: color 0.12s ease;
}
.s4-icon:hover:not(:disabled) { color: #23272c; }
.s4-icon:focus-visible { outline: 2px solid #7a8492; outline-offset: 1px; }
.s4-icon:disabled { color: #d2d5d9; cursor: not-allowed; }
.s4-icon-xs { font-size: 10px; }
.s4-icon-done { color: #15803d; }

/* icon-left rows under the address box */
.s4-row-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 0 0;
  font: inherit;
  font-size: 13px;
  color: #4a4f55;
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
}
.s4-row-ic { color: #8b9099; font-size: 11.5px; flex-shrink: 0; }
.s4-row-info strong { color: #23272c; font-weight: 600; }

/* label / value rows, left-aligned */
.s4-rows { display: grid; grid-template-columns: max-content 1fr; gap: 5px 16px; margin: 10px 0 0; font-size: 13px; }
.s4-rows dt { color: #666c73; }
.s4-rows dd { margin: 0; color: #23272c; word-break: break-word; }

.s4-close-box { background: #f3f4f6; padding: 18px 22px 20px; }
.s4-classify { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.s4-classify-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font: inherit;
  font-size: 12px;
  color: #23272c;
  background: #ffffff;
  border: 1px solid #d9dde2;
  border-radius: 4px;
  padding: 6px 11px;
  cursor: pointer;
  transition: border-color 0.12s ease;
}
.s4-classify-btn:hover:not(:disabled) { border-color: #8b9099; }
.s4-classify-btn:focus-visible { outline: 2px solid #7a8492; outline-offset: 1px; }
.s4-classify-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.s4-classify-active { border-color: #23272c; box-shadow: inset 0 0 0 1px #23272c; }
.s4-classify-status { min-height: 18px; margin-top: 8px; }

@media (max-width: 1023px) {
  .s4-body { flex-direction: column; }
  .s4-map { height: 44vh; flex: none; }
  .s4-panel { width: 100%; height: auto; border-left: 0; border-top: 1px solid #e6e8eb; }
  .s4-seg-close { margin-top: 0; }
}
</style>
